// ─────────────────────────────────────────────────────────────────────────────
// V2Ledger — /dashboard/ledgerpage (client ledger) in the v2 design.
// Endpoints are v1's: GET /api/ledger, GET /api/ledger/:clientId (paged + download),
// GET /api/invoices/client/:id, POST /:clientId/payment, POST /:clientId/multi-invoice-payment,
// DELETE /:clientId/entry/:entryId, DELETE /:clientId.
// The multi-invoice distribution preview is an exact port of v1's.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import usePageTitle from "../hooks/usePageTitle";
import userContext from "../context/user/userContext";
import API_BASE from "../apiConfig";
import { Glyph, Duo } from "./icons";
import { MenuButton, Money, Segmented, Sheet, Skel, Switch, inr, inrCompact, useMedia } from "./primitives";
import { Field, PageHead } from "./kit";

const PER_PAGE = 20;
const auth = () => ({ "auth-token": localStorage.getItem("token") });
const json = () => ({ "Content-Type": "application/json", ...auth() });
const today = () => new Date().toISOString().split("T")[0];
const fmtDate = (d, o = { day: "numeric", month: "short", year: "numeric" }) => (d ? new Date(d).toLocaleDateString("en-IN", o) : "—");
const MODES = ["Cash", "Bank Transfer", "UPI", "Cheque", "Card", "Other"];
const ini = (n = "") => n.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

function exportCSV(entries, clientName) {
  const rows = [["Date", "Description", "Debit", "Credit", "Balance"], ...entries.map((e) => [
    fmtDate(e.date), `"${(e.description || "").replace(/"/g, '""')}"`,
    e.type === "debit" ? e.amount.toFixed(2) : "", e.type === "credit" ? e.amount.toFixed(2) : "", (e.balance || 0).toFixed(2),
  ])];
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([rows.map((r) => r.join(",")).join("\n")], { type: "text/csv" }));
  a.download = `Ledger_${clientName}_${today()}.csv`;
  a.click();
}

// ══════════════════════════════════════════════════════════════════════════════
export default function V2Ledger({ showAlert }) {
  usePageTitle("Ledger");
  const { hasPermission } = useContext(userContext);
  const canWrite = hasPermission("ledger", "write");
  const isNarrow = useMedia("(max-width: 900px)");

  const [clients, setClients] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("due");
  const [selId, setSelId] = useState(null);

  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({ totalDebit: 0, totalCredit: 0, balance: 0 });
  const [pg, setPg] = useState(null);
  const [page, setPage] = useState(1);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailErr, setDetailErr] = useState("");
  const [unpaid, setUnpaid] = useState([]);

  const [payOpen, setPayOpen] = useState(false);
  const [dlOpen, setDlOpen] = useState(false);
  const [confirm, setConfirm] = useState(null); // { kind: "entry"|"ledger", entry? , client? }

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await fetch(`${API_BASE}/api/ledger`, { headers: auth() });
      const d = await r.json();
      setClients(Array.isArray(d) ? d : []);
    } catch { setClients([]); }
    finally { setLoadingList(false); }
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const selected = clients.find((c) => c._id === selId) || null;

  const loadDetail = useCallback(async (id, p = 1) => {
    if (!id) return;
    setLoadingDetail(true); setDetailErr("");
    try {
      const r = await fetch(`${API_BASE}/api/ledger/${id}?${new URLSearchParams({ page: p, limit: PER_PAGE })}`, { headers: auth() });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setEntries(Array.isArray(d.entries) ? d.entries : []);
      setPg(d.pagination || null); setPage(p);
      if (d.summary) setSummary({ totalDebit: d.summary.totalDebit || 0, totalCredit: d.summary.totalCredit || 0, balance: d.summary.balance || 0 });
      const ir = await fetch(`${API_BASE}/api/invoices/client/${id}`, { headers: auth() });
      const inv = ir.ok ? await ir.json() : [];
      setUnpaid((Array.isArray(inv) ? inv : []).filter((i) => i.paymentStatus === "UNPAID" || i.paymentStatus === "PARTIAL"));
    } catch { setDetailErr("Couldn't load this ledger."); setEntries([]); }
    finally { setLoadingDetail(false); }
  }, []);
  useEffect(() => { if (selId) loadDetail(selId, 1); }, [selId, loadDetail]);

  // Desktop: auto-select the client who owes the most
  const sorted = useMemo(() => {
    const s = q.trim().toLowerCase();
    const arr = clients.filter((c) => !s || (c.clientName || "").toLowerCase().includes(s) || (c.clientGSTIN || "").toLowerCase().includes(s));
    return arr.sort((a, b) => (sort === "due" ? (b.balance || 0) - (a.balance || 0) : (a.clientName || "").localeCompare(b.clientName || "")));
  }, [clients, q, sort]);
  useEffect(() => { if (!isNarrow && !selId && sorted.length) setSelId(sorted[0]._id); }, [sorted, isNarrow, selId]);

  const totalOutstanding = clients.reduce((s, c) => s + Math.max(c.balance || 0, 0), 0);
  const withDues = clients.filter((c) => (c.balance || 0) > 0).length;

  const runConfirm = async () => {
    const c = confirm;
    try {
      const url = c.kind === "entry" ? `${API_BASE}/api/ledger/${selId}/entry/${c.entry._id}` : `${API_BASE}/api/ledger/${c.client._id}`;
      const r = await fetch(url, { method: "DELETE", headers: auth() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't delete");
      setConfirm(null);
      showAlert?.(c.kind === "entry" ? "Entry deleted" : "Ledger cleared", "success");
      await loadList();
      if (selId) loadDetail(selId, 1);
    } catch (e) { setConfirm((x) => (x ? { ...x, error: e.message } : x)); }
  };

  // ── Detail pane ────────────────────────────────────────────────────────────
  const detail = selected && (
    <div className="v2l-detail" key={selected._id}>
      <div className="v2l-hero">
        <span className="v2-avatar v2-avatar--client" style={{ width: 56, height: 56, fontSize: 20 }}>{ini(selected.clientName)}</span>
        <div className="v2l-hero-text">
          <h2>{selected.clientName}</h2>
          {selected.clientGSTIN && <span className="v2-row-sub v2-mono">{selected.clientGSTIN}</span>}
        </div>
        <div className="v2l-hero-actions">
          {canWrite && <button type="button" className="v2-btn v2-btn--primary" onClick={() => setPayOpen(true)}><Glyph name="cash" size={16} /> Record payment</button>}
          <MenuButton className="v2-icon-btn v2-icon-btn--soft" label="Ledger options" width={240} items={[
            { label: "Download CSV", glyph: "download", onSelect: () => setDlOpen(true) },
            { label: "Print statement", glyph: "invoice", onSelect: () => window.print() },
            ...(canWrite ? ["divider", { label: "Clear this ledger", glyph: "close", danger: true, onSelect: () => setConfirm({ kind: "ledger", client: selected }) }] : []),
          ]} />
        </div>
      </div>

      <div className="v2l-stats">
        <div className="v2l-stat"><span>Billed</span><strong>{inr(summary.totalDebit, { decimals: 2 })}</strong></div>
        <div className="v2l-stat"><span>Received</span><strong className="is-good">{inr(summary.totalCredit, { decimals: 2 })}</strong></div>
        <div className={`v2l-stat v2l-stat--hero${summary.balance > 0 ? "" : " is-clear"}`}>
          <span>{summary.balance > 0 ? "Balance due" : summary.balance < 0 ? "In credit" : "Settled"}</span>
          <strong><Money value={Math.abs(summary.balance)} /></strong>
        </div>
      </div>
      {(selected.creditBalance || 0) > 0 && (
        <div className="v2-banner v2-banner--good"><Glyph name="sparkle" size={16} /> {inr(selected.creditBalance, { decimals: 2 })} advance / credit balance available</div>
      )}
      {unpaid.length > 0 && (
        <button type="button" className="v2l-unpaid" onClick={() => canWrite && setPayOpen(true)}>
          <Duo name="invoice" size={26} />
          <span><b>{unpaid.length} unpaid invoice{unpaid.length === 1 ? "" : "s"}</b> · {inr(unpaid.reduce((s, i) => s + (i.grandTotal - i.amountPaid), 0), { decimals: 2 })} outstanding</span>
          {canWrite && <span className="v2l-unpaid-cta">Settle <Glyph name="chevronRight" size={14} stroke={2} /></span>}
        </button>
      )}

      <div className="v2c-section-title">Statement</div>
      {detailErr && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {detailErr}</div>}
      <div className={`v2-card v2l-statement${loadingDetail ? " is-refreshing" : ""}`}>
        {loadingDetail && !entries.length ? <div className="v2-list" style={{ padding: 8 }}>{[0, 1, 2, 3, 4].map((i) => <Skel key={i} h={52} r={14} />)}</div>
          : entries.length === 0 ? <div className="v2c-empty"><Duo name="ledger" size={44} /><span>No transactions yet.</span></div>
          : entries.map((e) => {
            const credit = e.type === "credit";
            return (
              <div key={e._id} className="v2l-entry">
                <span className="v2pub-day"><strong>{new Date(e.date).getDate()}</strong><span>{fmtDate(e.date, { month: "short" })}</span></span>
                <span className="v2l-entry-main">
                  <span className="v2l-entry-desc">{e.description || (credit ? "Payment received" : "Invoice")}</span>
                  <span className="v2l-entry-meta">
                    {fmtDate(e.date, { year: "numeric" })}
                    {e.isCreditNote && <span className="v2l-tag">Credit note</span>}
                    {!e.isCreditNote && e.paymentMode && <span className="v2l-tag">{e.paymentMode}</span>}
                    {credit && e.isInvoicePayment && <span className="v2l-tag v2l-tag--link">Against invoice</span>}
                  </span>
                </span>
                <span className="v2l-entry-amts">
                  <span className={`v2l-amt${credit ? " is-credit" : ""}`}>{credit ? "−" : "+"}{inr(e.amount, { decimals: 2 })}</span>
                  <span className="v2l-bal">Bal {inr(e.balance, { decimals: 2 })}</span>
                </span>
                {canWrite && (
                  <MenuButton className="v2-icon-btn v2i-more" label="Entry options" width={210}
                    items={[{ label: "Delete entry", glyph: "close", danger: true, onSelect: () => setConfirm({ kind: "entry", entry: e }) }]} />
                )}
              </div>
            );
          })}
      </div>
      {pg && pg.totalPages > 1 && (
        <nav className="v2i-pager">
          <button type="button" className="v2-btn v2-btn--sm" disabled={page <= 1 || loadingDetail} onClick={() => loadDetail(selId, page - 1)}><Glyph name="chevronLeft" size={16} stroke={2} /> Newer</button>
          <span>Page <b>{page}</b> of {pg.totalPages}</span>
          <button type="button" className="v2-btn v2-btn--sm" disabled={page >= pg.totalPages || loadingDetail} onClick={() => loadDetail(selId, page + 1)}>Older <Glyph name="chevronRight" size={16} stroke={2} /></button>
        </nav>
      )}
    </div>
  );

  const showDetailOnly = isNarrow && selected;

  return (
    <div className="v2p v2l">
      {!showDetailOnly && (
        <PageHead title="Client ledger" eyebrow="Receivables"
          sub={loadingList ? "Loading…" : `${inr(totalOutstanding)} outstanding across ${withDues} client${withDues === 1 ? "" : "s"}`} />
      )}
      {showDetailOnly && (
        <button type="button" className="v2l-back" onClick={() => setSelId(null)}><Glyph name="chevronLeft" size={18} stroke={2} /> All clients</button>
      )}

      <div className={`v2c-split${showDetailOnly ? " is-detail" : ""}`}>
        {!showDetailOnly && (
          <section className="v2-card v2c-listcard" aria-label="Clients">
            <div className="v2-field--search v2c-search">
              <Glyph name="search" size={17} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients" aria-label="Search clients" />
            </div>
            <div className="v2l-sort"><Segmented size="sm" ariaLabel="Sort" value={sort} onChange={setSort} options={[{ value: "due", label: "Most due" }, { value: "name", label: "A–Z" }]} /></div>
            {loadingList ? <div className="v2-list" style={{ marginTop: 10 }}>{[0, 1, 2, 3, 4].map((i) => <Skel key={i} h={54} r={16} />)}</div> : (
              <div className="v2c-list">
                {sorted.map((c) => (
                  <button key={c._id} type="button" className={`v2l-row${c._id === selId && !isNarrow ? " is-sel" : ""}`} onClick={() => setSelId(c._id)}>
                    <span className="v2-avatar v2-avatar--client" style={{ width: 38, height: 38, fontSize: 13.5 }}>{ini(c.clientName)}</span>
                    <span className="v2-row-main">
                      <span className="v2-row-title">{c.clientName}</span>
                      <span className="v2-row-sub">{(c.balance || 0) > 0 ? "Owes you" : (c.balance || 0) < 0 ? "In credit" : "Settled"}</span>
                    </span>
                    <span className={`v2l-row-bal${(c.balance || 0) > 0 ? " is-due" : ""}`}>{inrCompact(Math.abs(c.balance || 0))}</span>
                  </button>
                ))}
                {sorted.length === 0 && <div className="v2c-empty"><span>No clients match.</span></div>}
              </div>
            )}
          </section>
        )}
        {(!isNarrow || selected) && (
          <section className={isNarrow ? "" : "v2-card v2c-detailcard"} aria-label="Ledger">
            {detail || <div className="v2c-empty"><Duo name="ledger" size={44} /><span>Choose a client to see their statement.</span></div>}
          </section>
        )}
      </div>

      {selected && (
        <PaymentSheet open={payOpen} client={selected} unpaid={unpaid} onClose={() => setPayOpen(false)}
          onDone={async (msg) => { setPayOpen(false); showAlert?.(msg, "success"); await loadList(); loadDetail(selId, 1); }} />
      )}
      {selected && <DownloadSheet open={dlOpen} client={selected} onClose={() => setDlOpen(false)} />}

      <Sheet open={!!confirm} onClose={() => setConfirm(null)} width={440}
        title={confirm?.kind === "entry" ? "Delete this entry?" : `Clear ${confirm?.client?.clientName || ""}'s ledger?`}
        footer={<>
          <button type="button" className="v2-btn" onClick={() => setConfirm(null)}>Keep</button>
          <button type="button" className="v2-btn v2c-btn-danger" onClick={runConfirm}>Delete</button>
        </>}>
        <p className="v2c-remove-text">
          {confirm?.kind === "entry"
            ? `${confirm.entry?.description || "This entry"} (${inr(confirm.entry?.amount, { decimals: 2 })}) will be removed and the running balance recalculated. Invoices themselves aren't changed.`
            : "Every ledger entry for this client will be deleted. Invoices themselves aren't changed, but balances shown here will reset."}
        </p>
        {confirm?.error && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {confirm.error}</div>}
      </Sheet>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Payment sheet — "Against invoices" (multi-invoice, with v1's distribution
// preview) or "On account" (plain ledger payment).
// ══════════════════════════════════════════════════════════════════════════════
function PaymentSheet({ open, client, unpaid, onClose, onDone }) {
  const [mode, setMode] = useState("invoices");
  const [ids, setIds] = useState(() => new Set());
  const [amount, setAmount] = useState("");
  const [payMode, setPayMode] = useState("Cash");
  const [date, setDate] = useState(today());
  const [description, setDescription] = useState("");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [full, setFull] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const wasOpen = useRef(false);

  useEffect(() => {
    if (open && !wasOpen.current) {
      setMode(unpaid.length ? "invoices" : "account"); setIds(new Set()); setAmount(""); setPayMode("Cash");
      setDate(today()); setDescription(""); setReference(""); setNote(""); setFull(false); setErr("");
    }
    wasOpen.current = open;
  }, [open, unpaid.length]);

  const sortedInv = useMemo(() => [...unpaid].sort((a, b) => new Date(a.invoiceDate) - new Date(b.invoiceDate)), [unpaid]);
  const sel = sortedInv.filter((i) => ids.has(i._id));
  const cash = parseFloat(amount) || 0;
  const selDue = sel.reduce((s, i) => s + (i.grandTotal - i.amountPaid), 0);

  // ── exact port of v1's preview ──
  let dist = [], tdsTotal = 0, excess = 0;
  if (sel.length && cash > 0) {
    if (full) {
      const totalDue = selDue;
      tdsTotal = Math.max(0, totalDue - cash);
      excess = Math.max(0, cash - totalDue);
      let used = 0;
      sel.forEach((inv, i) => {
        const bal = inv.grandTotal - inv.amountPaid;
        let tds;
        if (i === sel.length - 1) tds = parseFloat((tdsTotal - used).toFixed(2));
        else { tds = totalDue > 0 ? parseFloat(((bal / totalDue) * tdsTotal).toFixed(2)) : 0; used += tds; }
        dist.push({ inv, cash: parseFloat((bal - tds).toFixed(2)), tds, status: "PAID" });
      });
    } else {
      let rem = cash;
      sel.forEach((inv) => {
        const bal = inv.grandTotal - inv.amountPaid;
        const a = parseFloat(Math.min(rem, bal).toFixed(2));
        rem = parseFloat((rem - a).toFixed(2));
        dist.push({ inv, cash: a, tds: 0, status: a >= bal - 0.01 ? "PAID" : a > 0 ? "PARTIAL" : "SKIP" });
      });
      excess = Math.max(0, rem);
    }
  }

  const toggle = (id) => setIds((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!(cash > 0)) { setErr("Enter an amount greater than 0."); return; }
    if (mode === "invoices" && !ids.size) { setErr("Select at least one invoice."); return; }
    setBusy(true);
    try {
      const url = mode === "invoices" ? `${API_BASE}/api/ledger/${client._id}/multi-invoice-payment` : `${API_BASE}/api/ledger/${client._id}/payment`;
      const body = mode === "invoices"
        ? { invoiceIds: [...ids], amount: cash, paymentMode: payMode, date, description, reference, note, isFullPayment: full }
        : { amount: cash, description, paymentMode: payMode, date };
      const r = await fetch(url, { method: "POST", headers: json(), body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d.error || "Couldn't record the payment."); return; }
      if (mode === "invoices") {
        const parts = [`Payment applied to ${d.results?.length || ids.size} invoice${(d.results?.length || ids.size) === 1 ? "" : "s"}`];
        if ((d.totalTDS || 0) > 0) parts.push(`TDS ${inr(d.totalTDS)}`);
        if ((d.excess || 0) > 0) parts.push(`${inr(d.excess)} to credit balance`);
        onDone(parts.join(" · "));
      } else onDone("Payment recorded");
    } catch { setErr("Network error — nothing was recorded."); }
    finally { setBusy(false); }
  };

  return (
    <Sheet open={open} onClose={onClose} title="Record payment" subtitle={client.clientName} width={620}
      footer={<>
        <button type="button" className="v2-btn" onClick={onClose}>Cancel</button>
        <button type="submit" form="v2l-pay" className="v2-btn v2-btn--primary" disabled={busy}>
          {busy ? "Saving…" : cash > 0 ? `Record ${inr(cash, { decimals: 2 })}${tdsTotal > 0 ? ` + ${inr(tdsTotal)} TDS` : ""}` : "Record payment"}
        </button>
      </>}>
      <form id="v2l-pay" className="v2-form" onSubmit={submit}>
        <Segmented ariaLabel="Payment type" value={mode} onChange={setMode}
          options={[{ value: "invoices", label: `Against invoices${unpaid.length ? ` · ${unpaid.length}` : ""}` }, { value: "account", label: "On account" }]} />

        {mode === "invoices" && (
          sortedInv.length === 0 ? <div className="v2-banner v2-banner--good"><Glyph name="check" size={16} /> No unpaid invoices — record this on account instead.</div> : (
            <div className="v2-inset v2l-pick">
              <div className="v2l-pick-head">
                <span>Oldest first — cash is applied top-down</span>
                <button type="button" className="v2k-link" onClick={() => setIds(ids.size === sortedInv.length ? new Set() : new Set(sortedInv.map((i) => i._id)))}>
                  {ids.size === sortedInv.length ? "Clear" : "Select all"}
                </button>
              </div>
              {sortedInv.map((inv) => {
                const d = dist.find((x) => x.inv._id === inv._id);
                const on = ids.has(inv._id);
                return (
                  <label key={inv._id} className={`v2-inset-row v2l-pick-row${on ? " is-on" : ""}`}>
                    <span className="v2i-check"><input type="checkbox" checked={on} onChange={() => toggle(inv._id)} /><span><Glyph name="check" size={12} stroke={3} /></span></span>
                    <span className="v2-row-main">
                      <span className="v2-row-title">{inv.invoiceNumber}</span>
                      <span className="v2-row-sub">{fmtDate(inv.invoiceDate)} · due {inr(inv.grandTotal - inv.amountPaid, { decimals: 2 })}</span>
                    </span>
                    {d && (
                      <span className="v2l-pick-dist">
                        <b>{inr(d.cash, { decimals: 2 })}</b>
                        {d.tds > 0 && <span>+ {inr(d.tds, { decimals: 2 })} TDS</span>}
                        <span className={`v2-status v2-status--${d.status === "PAID" ? "good" : d.status === "PARTIAL" ? "warn" : "bad"}`}>{d.status === "PAID" ? "Paid" : d.status === "PARTIAL" ? "Part" : "Skipped"}</span>
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          )
        )}

        <Field label={mode === "invoices" ? "Cash received (after any TDS)" : "Amount received"}>
          <span className="v2-input-wrap v2-input-wrap--money">
            <span className="v2-input-prefix">₹</span>
            <input className="v2-input v2-input--lg" type="number" step="0.01" min="0" inputMode="decimal" value={amount}
              onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
            {mode === "invoices" && selDue > 0 && <button type="button" className="v2-chip-btn" onClick={() => setAmount(String(selDue.toFixed(2)))}>Full due</button>}
          </span>
        </Field>

        {mode === "invoices" && (
          <div className="v2-inset">
            <Switch id="v2l-full" checked={full} onChange={setFull} label="Settles the selected invoices"
              description="Shortfall is recorded as TDS, split by each invoice's share of the dues" />
          </div>
        )}
        {mode === "invoices" && excess > 0 && (
          <div className="v2-banner v2-banner--good"><Glyph name="sparkle" size={16} /> {inr(excess, { decimals: 2 })} more than due — it will be kept as credit balance.</div>
        )}

        <div className="v2-form-row">
          <Field label="Date"><input className="v2-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
          <Field label="Mode">
            <select className="v2-input" value={payMode} onChange={(e) => setPayMode(e.target.value)}>{MODES.map((m) => <option key={m}>{m}</option>)}</select>
          </Field>
        </div>
        <Field label="Description" optional><input className="v2-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={mode === "invoices" ? "e.g. NEFT for October invoices" : "e.g. Advance received"} /></Field>
        {mode === "invoices" && (
          <div className="v2-form-row">
            <Field label="Reference" optional><input className="v2-input" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="UTR / cheque no." /></Field>
            <Field label="Note" optional><input className="v2-input" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
          </div>
        )}
        {err && <div className="v2-banner v2-banner--bad" role="alert"><Glyph name="alert" size={16} /> {err}</div>}
      </form>
    </Sheet>
  );
}

function DownloadSheet({ open, client, onClose }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const go = async () => {
    setBusy(true); setErr("");
    try {
      const p = new URLSearchParams({ download: "true" });
      if (from) p.set("from", from);
      if (to) p.set("to", to);
      const r = await fetch(`${API_BASE}/api/ledger/${client._id}?${p}`, { headers: auth() });
      if (!r.ok) throw new Error();
      const d = await r.json();
      exportCSV(Array.isArray(d.entries) ? d.entries : [], client.clientName);
      onClose();
    } catch { setErr("Couldn't download the ledger."); }
    finally { setBusy(false); }
  };
  return (
    <Sheet open={open} onClose={onClose} title="Download ledger" subtitle={`${client.clientName} · CSV`} width={460}
      footer={<><button type="button" className="v2-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="v2-btn v2-btn--primary" disabled={busy} onClick={go}>{busy ? "Preparing…" : "Download CSV"}</button></>}>
      <div className="v2-form">
        <p className="v2-row-sub">Leave the dates empty for the full history.</p>
        <div className="v2-form-row">
          <Field label="From" optional><input className="v2-input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
          <Field label="To" optional><input className="v2-input" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        </div>
        {err && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {err}</div>}
      </div>
    </Sheet>
  );
}
